import { SxProps, Theme } from "@mui/material";
import { SystemStyleObject } from "@mui/system";
import { button, white } from "theme/colors";
import appModalTokens, { ModalSize, ModalTone } from "./AppModal.tokens";

const t = appModalTokens;

/* ── Shell ───────────────────────────────────────────────────────────────── */

/**
 * Root override. The scrim and the container's alignment both live here rather
 * than on `BackdropProps` so a single `sx` carries the whole overlay and the
 * sheet variant only has to flip one value.
 */
// `SystemStyleObject`, not `SxProps`: this is composed into an `sx` array so a
// caller can layer a `zIndex` on top, and `SxProps` admits an array itself —
// which makes the nested form MUI rejects type-check as TS2322.
export const rootSx = (isSheet: boolean): SystemStyleObject<Theme> => ({
  "& .MuiBackdrop-root": {
    backgroundColor: t.scrim.background,
    backdropFilter: t.scrim.blur,
  },
  "& .MuiDialog-container": {
    alignItems: isSheet ? "flex-end" : "center",
  },
});

/**
 * The dialog card itself.
 *
 * `@keyframes` blocks are declared inline: emotion scopes the name to this rule,
 * so the enter animation cannot collide with anything else on the page.
 */
export const paperSx = (opts: {
  size: ModalSize;
  width?: number;
  isSheet: boolean;
  fullScreen: boolean;
}): SystemStyleObject<Theme> => {
  const { size, width, isSheet, fullScreen } = opts;

  if (fullScreen) {
    return {
      backgroundColor: t.color.surface,
      backgroundImage: "none",
      borderRadius: 0,
      boxShadow: "none",
      display: "flex",
      flexDirection: "column",
    };
  }

  if (isSheet) {
    return {
      "@keyframes appSheetIn": {
        from: { transform: "translateY(100%)" },
        to: { transform: "none" },
      },
      m: 0,
      width: "100%",
      maxWidth: "100%",
      maxHeight: t.sheet.maxHeight,
      display: "flex",
      flexDirection: "column",
      backgroundColor: t.color.surface,
      backgroundImage: "none",
      borderRadius: t.radius.sheet,
      border: "none",
      boxShadow: t.shadow.sheet,
      overflow: "hidden",
      animation: `appSheetIn ${t.motion.sheetIn}`,
      "@media (prefers-reduced-motion: reduce)": { animation: "none" },
    };
  }

  return {
    "@keyframes appModalIn": {
      from: { opacity: 0, transform: "translateY(12px) scale(.985)" },
      to: { opacity: 1, transform: "none" },
    },
    // The width has to subtract the paper's own margins — a plain `100%` would
    // overflow the viewport horizontally on a narrow-but-not-sheet screen
    // (tablet portrait), since margin sits outside the declared width. This is
    // the same arithmetic MUI's own `fullWidth` does.
    width: { xs: "calc(100% - 40px)", sm: "calc(100% - 64px)" },
    maxWidth: width ?? t.size[size],
    m: { xs: "20px", sm: "32px" },
    display: "flex",
    flexDirection: "column",
    backgroundColor: t.color.surface,
    backgroundImage: "none",
    borderRadius: t.radius.shell,
    border: `1px solid ${t.color.border}`,
    boxShadow: t.shadow.shell,
    overflow: "hidden",
    animation: `appModalIn ${t.motion.shellIn}`,
    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
  };
};

/* ── Header ──────────────────────────────────────────────────────────────── */

/**
 * `center`, not `flex-start`: the icon tile is 40px and a one-line title only
 * about 26px, so top-aligning them left the title sitting against the top of
 * the tile rather than across its middle. With a subtitle the tile centres
 * against the whole text block.
 *
 * The close button opts back out via `alignSelf` in `closeButtonSx` — it
 * belongs at the top of the header, not halfway down a two-line one.
 */
export const headerSx = (isSheet: boolean): SystemStyleObject<Theme> => ({
  display: "flex",
  alignItems: "center",
  gap: t.header.gap,
  p: isSheet ? t.header.sheetPadding : t.header.padding,
  flexShrink: 0,
});

export const iconTileSx = (tone: ModalTone): SxProps<Theme> => ({
  width: t.header.tileSize,
  height: t.header.tileSize,
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: t.radius.iconTile,
  backgroundColor: t.tone[tone].tile,
  color: t.tone[tone].glyph,
});

/** Grows to fill whatever the tile and close button leave behind. */
export const headerTextSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
};

export const titleSx = (isSheet: boolean): SxProps<Theme> => ({
  m: 0,
  p: 0,
  color: t.color.ink,
  fontSize: isSheet ? t.font.sheetTitle : t.font.title,
  fontWeight: t.weight.heading,
  letterSpacing: t.letterSpacing.title,
  lineHeight: 1.3,
});

export const subtitleSx = (isSheet: boolean): SxProps<Theme> => ({
  mt: t.header.titleGap,
  color: t.color.muted,
  fontSize: isSheet ? t.font.sheetSubtitle : t.font.subtitle,
  fontWeight: 500,
  lineHeight: 1.45,
});

export const closeButtonSx: SxProps<Theme> = {
  width: t.header.closeSize,
  height: t.header.closeSize,
  p: 0,
  flexShrink: 0,
  // The header centres its row so the title lines up with the icon tile; the
  // close button keeps its original top-right seat regardless of how tall the
  // title block grows.
  alignSelf: "flex-start",
  borderRadius: t.radius.close,
  color: t.color.muted,
  "&:hover": {
    backgroundColor: t.color.closeHoverBg,
    color: t.color.ink,
  },
};

/* ── Body ────────────────────────────────────────────────────────────────── */

export const bodySx = (opts: {
  isSheet: boolean;
  scrollable: boolean;
  disablePadding: boolean;
  hasHeader: boolean;
}): SystemStyleObject<Theme> => {
  const { isSheet, scrollable, disablePadding, hasHeader } = opts;
  const padding = isSheet ? t.body.sheetPadding : t.body.padding;

  return {
    // `DialogContent` ships a `padding-top` that only lands when it follows a
    // `DialogTitle`; this shell draws its own header, so the shorthand has to be
    // restated one specificity step up.
    "&.MuiDialogContent-root": {
      p: disablePadding ? 0 : padding,
      // Without a header the body owns the top inset the header would have had.
      pt: disablePadding ? 0 : hasHeader ? undefined : padding.split(" ")[0],
    },
    color: t.color.body,
    ...(scrollable
      ? { maxHeight: isSheet ? "none" : t.body.maxHeight, overflowY: "auto" }
      : { overflowY: "visible" }),
    ...(isSheet ? { flex: "1 1 auto", overflowY: "auto" } : {}),
  };
};

/**
 * Plain-prose body copy — the `text` body in the mock.
 *
 * Typed as a single style object rather than `SxProps` so screens can compose it
 * with the array form, `sx={[bodyTextSx, { mb: 2 }]}` — `SxProps` admits an
 * array itself and so cannot be an array element.
 */
export const bodyTextSx: SystemStyleObject<Theme> = {
  m: 0,
  color: t.color.body,
  fontSize: t.font.body,
  fontWeight: 500,
  lineHeight: t.body.lineHeight,
};

/**
 * Read-only summary block inside a body (the success preset's detail list).
 * Exported so screens get the mock's inner surface without inventing their own.
 */
export const innerSurfaceSx: SystemStyleObject<Theme> = {
  backgroundColor: t.color.innerSurface,
  border: `1px solid ${t.color.innerBorder}`,
  borderRadius: t.radius.inner,
  p: "16px",
};

/* ── Footer ──────────────────────────────────────────────────────────────── */

/**
 * Right-aligned action bar. On the sheet the buttons stretch to fill the row so
 * they stay thumb-sized, but the source order — secondary first, primary last —
 * never changes.
 */
export const footerSx = (opts: {
  isSheet: boolean;
  hasStart: boolean;
}): SystemStyleObject<Theme> => ({
  display: "flex",
  alignItems: "center",
  justifyContent: opts.hasStart ? "space-between" : "flex-end",
  gap: t.footer.gap,
  p: opts.isSheet ? t.footer.sheetPadding : t.footer.padding,
  flexShrink: 0,
  borderTop: `1px solid ${t.color.hairline}`,
  backgroundColor: opts.isSheet ? t.color.surface : t.color.footerBg,
  ...(opts.isSheet
    ? { "& > .MuiButton-root": { flex: "1 1 0", minWidth: 0 } }
    : {}),
});

/** Holds the trailing buttons together when a `footerStart` slot is present. */
export const footerActionsSx = (isSheet: boolean): SystemStyleObject<Theme> => ({
  display: "flex",
  alignItems: "center",
  gap: t.footer.gap,
  ...(isSheet
    ? { flex: "1 1 auto", "& > .MuiButton-root": { flex: "1 1 0", minWidth: 0 } }
    : {}),
});

/**
 * Destructive primary. `MuiButton.contained` paints with the `background`
 * shorthand, which emotion emits after an `sx` longhand and would reset the
 * fill — so it is pinned one specificity step higher, the same guard the
 * re-skinned screens use.
 */
export const destructiveButtonSx: SystemStyleObject<Theme> = {
  "&.MuiButton-root": {
    background: t.destructive.fill,
    color: white,
    "&:hover": { background: t.destructive.fill, boxShadow: "none" },
    "&:active": { background: t.destructive.activeFill },
    // Mirrors the global `MuiButton` containedPrimary override: without this,
    // this rule's own hardcoded `background` above beats the theme's disabled
    // treatment (same specificity, declared later), so a disabled/loading
    // primary button would stay fully red instead of the app's standard
    // muted-lilac disabled look.
    "&.Mui-disabled": {
      background: button.disabledBg,
      color: button.disabledColor,
      boxShadow: "none",
    },
  },
};

/* ── Sheet chrome ────────────────────────────────────────────────────────── */

export const grabberWrapSx: SxProps<Theme> = {
  display: "flex",
  justifyContent: "center",
  p: t.sheet.grabberPadding,
  flexShrink: 0,
};

export const grabberSx: SxProps<Theme> = {
  width: t.sheet.grabberWidth,
  height: t.sheet.grabberHeight,
  borderRadius: t.radius.grabber,
  backgroundColor: t.color.grabber,
};
